import { isEmpty, isNil, omit } from "lodash-es";
import { ensure, ensureArray, stringType, TType } from "types";

import { IURLSearchParams, urlSearchParamsConfig, urlSortConfigType } from "@/core/url";
import { codelistType, ICodelist } from "@/data/codelists";
import { ILocation } from "@/data/locations";
import { IRole } from "@/data/roles";
import { ISkill } from "@/data/skills";

export interface IConsultantProfileURLFilter {
  location?: ILocation;
  roleIds?: IRole[];
  skillIds?: ISkill[];
  status?: ICodelist[];
  permissions?: ICodelist[];
  query?: string;
  contactPersonProfileIds?: Array<number>;
}

export const configConsultantProfileURLFilterType: TType<IConsultantProfileURLFilter> = {
  query: stringType(undefined),
  status: v => (isEmpty(v) ? undefined : ensureArray(codelistType)(v)),
  permissions: v => (isEmpty(v) ? undefined : ensureArray(codelistType)(v)),
  contactPersonProfileIds: v => (!isEmpty(v) ? v : undefined),
};

export const consultantProfileURLFilterType = ensure(configConsultantProfileURLFilterType);

export interface IConsultantProfileListFiltersConfig {
  filters: IConsultantProfileURLFilter;
}

const configConsultantProfileListFiltersConfig: TType<IConsultantProfileListFiltersConfig> = {
  filters: v => v || {},
};

export const consultantProfileListFiltersConfig = ensure(configConsultantProfileListFiltersConfig);

const configTeamURLFilterType: TType<IConsultantProfileURLFilter> = {
  query: v => (v ? v : undefined),
  contactPersonProfileIds: v => (!isEmpty(v) ? v : undefined),
  status: v => (isEmpty(v) ? undefined : ensureArray(codelistType)(v)),
  permissions: v => (isEmpty(v) ? undefined : ensureArray(codelistType)(v)),
};

export const teamURLFilterType = ensure(configTeamURLFilterType);

export interface IConsultantProfileListSearchParams extends IURLSearchParams {
  filtersConfig: IConsultantProfileURLFilter;
}

const configConsultantProfileListSearchParams: TType<IConsultantProfileListSearchParams> = {
  ...urlSearchParamsConfig,
  sortConfig: v =>
    isNil(v)
      ? [
          {
            sortBy: "user.lastName",
            order: "ASC",
          },
          {
            sortBy: "user.firstName",
            order: "ASC",
          },
        ]
      : ensureArray(urlSortConfigType)(v),
  filtersConfig: consultantProfileURLFilterType,
};

export const consultantProfileListSearchParamsType = ensure(configConsultantProfileListSearchParams);

export const consultantProfileListFiltersMapper = {
  location: (v: ILocation) => (isNil(v) ? undefined : omit(v, "name")),
  roleIds: (v: IRole[]) => (isNil(v) ? undefined : v.map(r => r.id)),
  skillIds: (v: ISkill[]) => (isNil(v) ? undefined : v.map(s => s.id)),
  status: (v: ICodelist[]) => (isNil(v) ? undefined : v.map(s => s.parameter)),
  permissions: (v: ICodelist[]) => (isNil(v) ? undefined : v.map(s => s.code)),
  query: (v: string) => (isNil(v) ? undefined : v),
  contactPersonProfileIds: (v: Array<number>) => (isNil(v) ? undefined : v),
};

export const consultantProfileListFiltersEventMapper = {
  location: (v: ILocation) => (isNil(v) ? undefined : v.name),
  roleIds: (v: IRole[]) => (isNil(v) ? undefined : v.map(r => r.name)),
  skillIds: (v: ISkill[]) => (isNil(v) ? undefined : v.map(s => s.name)),
  status: (v: ICodelist[]) => (isNil(v) ? undefined : v.map(s => s.parameter)),
  permissions: (v: ICodelist[]) => (isNil(v) ? undefined : v.map(s => s.code)),
  query: (v: string) => (isNil(v) ? undefined : v),
  contactPersonProfileIds: (v: Array<number>) => (isNil(v) ? undefined : v),
};
